import { useEffect, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { equipmentApi } from '@entities/equipment';

import type { Warehouse } from '@entities/equipment';

interface UseWarehouseRenameParams {
    warehouse: Warehouse;
    hasFullAccess: boolean;
}

export const useWarehouseRename = ({ warehouse, hasFullAccess }: UseWarehouseRenameParams) => {
    const queryClient = useQueryClient();
    const [isRenameMode, setIsRenameMode] = useState(false);
    const [warehouseTitle, setWarehouseTitle] = useState(warehouse.title);
    const isRenameSavingRef = useRef(false);

    useEffect(() => {
        if (!isRenameMode) {
            setWarehouseTitle(warehouse.title);
        }
    }, [isRenameMode, warehouse.title]);

    const renameWarehouseMutation = useMutation({
        mutationFn: equipmentApi.renameWarehouse,
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getEquipmentDataQueryOptions().queryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getWarehouseListQueryOptions().queryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getTotalInfoQueryOptions().queryKey,
                }),
            ]);
        },
    });

    const startRename = () => {
        setWarehouseTitle(warehouse.title);
        setIsRenameMode(true);
    };

    const finishRename = async () => {
        const nextTitle = warehouseTitle.trim();

        if (isRenameSavingRef.current) {
            return;
        }

        if (!nextTitle || nextTitle === warehouse.title) {
            setWarehouseTitle(warehouse.title);
            setIsRenameMode(false);

            return;
        }

        isRenameSavingRef.current = true;

        try {
            await renameWarehouseMutation.mutateAsync({
                warehouseId: warehouse.id,
                title: nextTitle,
            });
            setIsRenameMode(false);
        } finally {
            isRenameSavingRef.current = false;
        }
    };

    return {
        isRenameMode,
        warehouseTitle,
        setWarehouseTitle,
        canRenameWarehouse: !warehouse.isAttachedToProject && hasFullAccess,
        startRename,
        finishRename,
    };
};
